<template>
  <div class="h-full flex flex-col min-h-0">
    <UCard
      ref="cardRef"
      :ui="{
        root: 'flex-1 min-h-0 flex flex-col',
        body: 'p-0 sm:p-0 flex-1 min-h-0 flex flex-col',
        header: 'p-0 sm:p-0 border-b border-gray-200 dark:border-gray-800'
      }"
    >
      <template #header>
        <slot name="header" />
        <div
          v-if="showSearch"
          class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-2.5"
          :class="{ 'border-t border-gray-200 dark:border-gray-800': $slots.header }"
        >
          <UInput
            v-model="searchTerm"
            icon="i-heroicons-magnifying-glass"
            placeholder="Suchen..."
            color="primary"
            variant="outline"
            class="w-full sm:max-w-xs"
            :ui="{
              trailing: 'pe-1',
            }"
          >
            <template #trailing>
              <UButton
                v-if="searchTerm"
                color="neutral"
                variant="ghost"
                icon="i-heroicons-x-mark"
                size="xs"
                @click="searchTerm = ''"
              />
            </template>
          </UInput>
          <div class="flex items-center gap-2">
            <slot name="header-actions" />
          </div>
        </div>
      </template>

      <UTable
        ref="tableRef"
        v-bind="$attrs"
        v-model:sorting="sorting"
        :columns="tableColumns"
        :column-sizing="columnSizing"
        :data="visibleData"
        :loading="loading"
        :manual-sorting="true"
        :total="total"
        sticky
        :virtualize="{
          overscan: 10,
          estimateSize: () => 48,
        }"
        class="flex-1 [scrollbar-gutter:stable]"
        :ui="{
          tr: 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0',
          base: 'table-fixed min-w-full',
          td: 'py-4 px-4'
        }"
        @select="onSelect"
      >
        <!-- Dynamic header slots for sorting -->
        <template v-for="col in normalizedSelectedColumns" :key="col.key" #[`${col.key}-header`]="{ column }">
          <slot
            :name="`${col.key}-header`"
            :column="column"
            :toggle-sort="() => toggleColumnSort(col.key, column)"
          >
            <div
              class="flex items-center gap-2 cursor-pointer select-none"
              :aria-label="`Sort by ${col.key}`"
              :aria-sort="sortAriaValue(col.key)"
              @click="toggleColumnSort(col.key, column)"
            >
              <span class="truncate">{{ col.label }}</span>
              <UIcon
                v-if="column.getIsSorted()"
                :name="column.getIsSorted() === 'asc' ? 'i-heroicons-bars-arrow-up' : 'i-heroicons-bars-arrow-down'"
                class="w-4 h-4 text-primary shrink-0"
              />
            </div>
          </slot>
        </template>

        <!-- Dynamic column slots -->
        <template v-for="col in normalizedSelectedColumns" :key="col.key" #[`${col.key}-cell`]="{ row }">
          <slot :name="`${col.key}-cell`" :row="row">
            <span v-if="isMonoField(col.key)" class="font-mono font-medium">
              {{ getValue(row.original, col.key) }}
            </span>
            <span v-else>{{ getValue(row.original, col.key) }}</span>
          </slot>
        </template>

        <!-- Pass through other slots -->
        <template v-for="name in otherSlots" :key="name" #[name]="slotData">
          <slot :name="name" v-bind="slotData" />
        </template>

        <template #body-bottom>
          <div v-if="loading && data.length > 0" class="p-4 flex justify-center w-full">
            <UIcon class="w-6 h-6 animate-spin text-primary" name="i-heroicons-arrow-path" />
          </div>
          <slot name="body-bottom" />
        </template>

        <template #empty>
          <div class="flex flex-col items-center justify-center py-12 text-muted px-4">
            <div class="mb-4">
              <UIcon class="w-12 h-12 sm:w-16 sm:h-16 text-primary/40" :name="emptyIcon" />
            </div>
            <div class="text-center space-y-2">
              <p class="text-lg sm:text-xl font-medium text-highlighted">{{ emptyTitle }}</p>
              <p class="text-xs sm:text-sm">{{ emptyDescription }}</p>
            </div>
          </div>
        </template>
      </UTable>
    </UCard>

    <ColumnSelectionModal
      ref="columnModal"
      :all-columns="allColumns"
      :initial-selected="normalizedSelectedColumns"
      :loading="columnsLoading"
      @save="onSaveColumns"
      @reset="onResetColumns"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, useTemplateRef, useSlots } from 'vue'
import ColumnSelectionModal from '@/components/ColumnSelectionModal.vue'

interface Column {
  key: string
  label: string
  locked?: boolean
  graphql?: string
}

interface SortingItem {
  id: string
  desc: boolean
}

const props = withDefaults(
  defineProps<{
    data: Record<string, unknown>[]
    loading?: boolean
    selectedColumns: Column[]
    allColumns: Column[]
    columnsLoading?: boolean
    emptyIcon?: string
    emptyTitle?: string
    emptyDescription?: string
    monoFields?: string[]
    total?: number
    showSearch?: boolean
    highlightRowKey?: string
    highlightRowValue?: string | number | boolean | null
    sortStorageKey?: string
  }>(),
  {
    loading: false,
    columnsLoading: false,
    emptyIcon: 'i-heroicons-magnifying-glass',
    emptyTitle: 'Keine Daten gefunden',
    emptyDescription: 'Es konnten keine Daten geladen werden.',
    monoFields: () => ['fldAdrNr', 'fldPrjNr', 'fldBelegNr'],
    total: 0,
    showSearch: true,
  },
)

const emit = defineEmits<{
  (e: 'select', row: { original: Record<string, unknown> }): void
  (e: 'load-more'): void
  (e: 'update:columns', columns: Column[]): void
  (e: 'reset:columns'): void
}>()

const columnModal = ref<InstanceType<typeof ColumnSelectionModal> | null>(null)
const tableRef = useTemplateRef('tableRef')
const cardRef = useTemplateRef('cardRef')

const searchTerm = ref('')

const normalizedSelectedColumns = computed(() => {
  const keys = new Set<string>()
  return props.selectedColumns.reduce<Column[]>((columns, column) => {
    const key = column.key.trim()
    if (!key || keys.has(key)) return columns

    keys.add(key)
    columns.push({ ...column, key, label: column.label || key })
    return columns
  }, [])
})

function loadSorting(storageKey?: string): SortingItem[] {
  if (!storageKey) return []
  const saved = localStorage.getItem(storageKey)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed)) {
        return parsed
      }
    } catch (e) {
      console.error('Failed to parse sorting from localStorage', e)
    }
  }
  return []
}

function persistSorting(storageKey: string | undefined, value: SortingItem[]) {
  if (!storageKey) return
  localStorage.setItem(storageKey, JSON.stringify(value))
}

const sorting = ref<SortingItem[]>(loadSorting(props.sortStorageKey))

function isNumericValue(value: unknown) {
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))
}

function isDateValue(value: unknown) {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(value) &&
    Number.isFinite(Date.parse(value))
  )
}

function compareValues(left: unknown, right: unknown) {
  if (left == null && right == null) return 0
  if (left == null) return 1
  if (right == null) return -1
  if (isNumericValue(left) && isNumericValue(right)) return Number(left) - Number(right)
  if (isDateValue(left) && isDateValue(right)) return Date.parse(String(left)) - Date.parse(String(right))
  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' })
}

const visibleData = computed(() => {
  const query = searchTerm.value.trim().toLowerCase()
  const filtered = query
    ? props.data.filter((row) =>
        normalizedSelectedColumns.value.some((column) =>
          String(getValue(row, column.key)).toLowerCase().includes(query),
        ),
      )
    : props.data

  const activeSort = sorting.value[0]
  if (!activeSort) return filtered

  const direction = activeSort.desc ? -1 : 1
  return filtered
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftValue = getValue(left.row, activeSort.id)
      const rightValue = getValue(right.row, activeSort.id)
      if (leftValue === '' && rightValue === '') return left.index - right.index
      if (leftValue === '') return 1
      if (rightValue === '') return -1
      const compared = compareValues(
        leftValue,
        rightValue,
      )
      return compared === 0 ? left.index - right.index : compared * direction
    })
    .map(({ row }) => row)
})

function sortAriaValue(columnKey: string) {
  const activeSort = sorting.value.find((item) => item.id === columnKey)
  if (!activeSort) return 'none'
  return activeSort.desc ? 'descending' : 'ascending'
}

interface SortableColumn {
  toggleSorting?: (desc?: boolean) => void
  clearSorting?: () => void
}

function toggleColumnSort(columnKey: string, column?: SortableColumn) {
  const current = sorting.value.find((item) => item.id === columnKey)
  if (!current) {
    sorting.value = [{ id: columnKey, desc: false }]
    if (column?.toggleSorting) {
      column.toggleSorting(false)
    }
  } else if (!current.desc) {
    sorting.value = [{ id: columnKey, desc: true }]
    if (column?.toggleSorting) {
      column.toggleSorting(true)
    }
  } else {
    sorting.value = []
    if (column?.clearSorting) {
      column.clearSorting()
    } else if (column?.toggleSorting) {
      column.toggleSorting(undefined)
    }
    const tableApi = (tableRef.value as { tableApi?: { setSorting?: (sorting: unknown[]) => void } } | null)?.tableApi
    if (tableApi?.setSorting) {
      tableApi.setSorting([])
    }
  }
}

watch(
  sorting,
  (val) => {
    persistSorting(props.sortStorageKey, val)
  },
  { deep: true },
)

watch(
  () => props.sortStorageKey,
  (key) => {
    sorting.value = loadSorting(key)
  },
)

const tableColumns = computed(() => {
  // Ensure re-evaluation when highlighting props change
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  props.highlightRowKey
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  props.highlightRowValue

  return normalizedSelectedColumns.value.map((col) => {
    const colKey = col.key || ''
    // To prevent jumping headers during virtualization, we provide fixed sizes.
    const isIdField =
      colKey === 'fldAdrNr' ||
      colKey === 'fldPrjNr' ||
      colKey === 'fldBelegNr' ||
      colKey.endsWith('.fldAdrNr') ||
      colKey === 'fldArt' ||
      colKey === 'fldPrio'
    const isStatus =
      colKey.toLowerCase().includes('status') || colKey.toLowerCase().includes('sts')

    const width = isIdField ? 120 : isStatus ? 150 : 250

    return {
      id: col.key,
      accessorKey: col.key,
      header: col.label,
      size: width,
      minSize: 80,
      enableSorting: true,
      enableGlobalFilter: true,
      meta: {
        class: {
          th: (isIdField ? 'w-[120px] min-w-[120px]' : isStatus ? 'w-[150px] min-w-[150px]' : 'w-[250px] min-w-[250px]') + ' max-w-[250px]',
          td: ({ row }: { row: { original: Record<string, unknown> } }) => {
            const base = (isIdField ? 'w-[120px] min-w-[120px]' : isStatus ? 'w-[150px] min-w-[150px]' : 'w-[250px] min-w-[250px]') + ' max-w-[250px] truncate'
            if (isHighlighted(row.original)) {
              return `${base} bg-primary-100 dark:bg-primary-900/40`
            }
            return base
          },
        },
        style: {
          th: { width: `${width}px`, minWidth: `${width}px` },
          td: { width: `${width}px`, minWidth: `${width}px` },
        },
      },
    }
  })
})

const columnSizing = computed(() => {
  const sizing: Record<string, number> = {}
  tableColumns.value.forEach((col) => {
    sizing[col.id] = col.size as number
  })
  return sizing
})

function isMonoField(key: string) {
  return props.monoFields.some((field) => key === field || key.endsWith('.' + field))
}

function isHighlighted(row: Record<string, unknown>) {
  const highlightKey = props.highlightRowKey
  const highlightValue = props.highlightRowValue

  if (!highlightKey || !highlightValue) return false

  const value = getValue(row, highlightKey)
  if (value) {
    const s1 = String(value).toLowerCase().replace(/[{}]/g, '').trim()
    const s2 = String(highlightValue).toLowerCase().replace(/[{}]/g, '').trim()
    if (s1 && s2 && s1 === s2) return true
  }

  return false
}

function getValue(obj: Record<string, unknown>, path: string) {
  if (!obj || !path) return ''
  const parts = path.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return ''
    current = (current as Record<string, unknown>)[part]
  }
  return (current as string | number | boolean | null) ?? ''
}

const slots = useSlots()

const otherSlots = computed(() => {
  const handled = ['header', 'header-actions', 'body-bottom', 'empty']
  normalizedSelectedColumns.value.forEach((col) => {
    if (col.key) {
      handled.push(`${col.key}-header`)
      handled.push(`${col.key}-cell`)
      handled.push(`cell-${col.key}`)
    }
  })
  return Object.keys(slots).filter((name) => !handled.includes(name))
})

function onSelect(_event: Event, row: { original: Record<string, unknown> }) {
  emit('select', row)
}

function onSaveColumns(columns: Column[]) {
  emit('update:columns', columns)
}

function onResetColumns() {
  emit('reset:columns')
}

function openColumnSelector() {
  columnModal.value?.open()
}

defineExpose({
  openColumnSelector,
  tableRef,
  toggleColumnSort,
  sorting,
})
</script>
